import { useState } from 'react'
import { message } from 'antd'
import { handleError } from '../../../../lib/utils'
import { getInvoices, getRemittances } from '../../../utils'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { readBillingRecords } from '../../../../Bookings/utils/utils'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import dayjs from 'dayjs'

/**
 * Hook for managing remittance form data and operations
 * @param {Object} initialData - Initial remittance data
 * @param {Object} form - Form instance
 * @param {Function} handleGetRemittances - Function to refresh remittances list
 */
export const useRemittanceForm = (initialData, form, handleGetRemittances) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [remittanceData, setRemittanceData] = useState(initialData || {})

  /**
   * Refreshes remittance data from the server
   */
  const handleRefresh = async () => {
    let tmpData = null
    setLoading(true)

    try {
      // Get remittance data
      tmpData = await getRemittances({ _id: initialData._id })

      // Get Patient Records
      const patientIds = tmpData[0].payments.map((item) => item.userRef).filter((id) => id && id !== '') // Filter out empty/invalid IDs
      const patients = patientIds.length > 0 ? await readPatients({ _id: { $in: patientIds } }) : []

      // Get Invoice Records - filter out empty/invalid invoice references
      const invoiceIds = tmpData[0].payments.map((item) => item.invoiceRef).filter((id) => id && id !== '') // Filter out empty/invalid IDs
      const invoices = invoiceIds.length > 0 ? await getInvoices({ _id: { $in: invoiceIds } }) : []

      // Get Billing Records - filter out undefined booking references
      const billingIds = invoices.map((item) => item.bookingRef).filter((id) => id && id !== '') // Filter out empty/invalid IDs
      const billingRecords = billingIds.length > 0 ? await readBillingRecords({ bookingRef: { $in: billingIds } }) : []

      // Merge Patient Records
      tmpData[0].payments = tmpData[0].payments.map((item) => {
        const patientRecord = patients.find((patient) => patient._id === item.userRef)
        const invoiceRecord = invoices.find((invoice) => invoice._id === item.invoiceRef)
        // Only try to filter billing records if we have a valid invoice record with bookingRef
        const tmpBillingRecords = invoiceRecord?.bookingRef
          ? billingRecords.filter((record) => record.bookingRef === invoiceRecord.bookingRef)
          : []

        return {
          ...item,
          patientRecord,
          invoiceRecord,
          billingRecords: tmpBillingRecords
        }
      })

      setRemittanceData(tmpData[0])

      // Set Form Data
      form.setFieldsValue({
        ...tmpData[0],
        remittanceDate: tmpData[0].remittanceDate ? dayjs(tmpData[0].remittanceDate).format('YYYY-MM-DD') : null
      })
    } catch (e) {
      message.error(handleError(e, true))
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handles closing the remittance tab
   */
  const handleCloseTab = () => {
    if (handleGetRemittances) {
      handleGetRemittances()
    }

    dispatch(
      coreReducer.actions.closeTab({
        targetKey: `${CoreEnums.tabKeys.REMITTANCES}_${initialData._id}`,
        removeBreadcrumb: true
      })
    )
  }

  return {
    loading,
    remittanceData,
    handleRefresh,
    handleCloseTab
  }
}
